import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, ViewRef} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../services/Toast.service";
import {StateService} from "../../services/StateService";
import {NumeralService} from "../../services/Numeral.service";
import {NgxScrollEvent} from "../../directives/ngx-scroll-event.directive";
import {SwitchBoard} from "../../services/SwitchBoard";
import {Router} from "@angular/router";
import {LoadingService} from "../../services/LoadingService";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {ManageTaxStrategiesServices} from "../../tpiq-services/manage-tax-strategies.service";
import {State} from "../../models/State";
import {takeUntil} from "rxjs/operators";
import {Session} from "../../services/Session";
import {PageTitleService} from "../../services/PageTitle";
import {TpiqSelectDropdownComponent} from "../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {TrackerPaginatedTableComponent} from "../../tables/tracker-paginated-table/tracker-paginated-table.component";
import {TpiqCustomDatepicker} from "../../directives/tpiq-custom-datepicker";

declare const _: any;

@Component({
  selector: 'app-manage-firms',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, TrackerPaginatedTableComponent, TpiqCustomDatepicker],
  templateUrl: './manage-firms.component.html',
  styleUrl: './manage-firms.component.scss'
})
export class ManageFirmsComponent implements OnInit, OnDestroy {
  org: string;
  user: string;
  taxStrategyTableData: any = {};
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  taxStrategiesSort: any = {};
  isLoading = true;
  nextIndex = 0;
  hasNextSet = false;
  hasTaxStrategyList = false;
  searchKey: string;
  canPublish = false;
  reveal: any = {
    editFirm: false
  };
  orderArray: any = {
    Name: 'firmName',
    User: 'ownerName',
    Email: 'ownerEmail'
  };
  editStrategy: any = {
    id: '',
    firmName: '',
    trialStartDate: '',
    trialEndDate: '',
    plan: '',
    status: '',
  };
  statusData: Array<any> = [
    {key: 'Closed', value: 'Closed'},
    {key: 'Paid', value: 'Paid'},
    {key: 'Trial', value: 'Trial'},
  ];
  plansData: Array<any> = [
    {plan: '$300'},
    {plan: '$450'},
    {plan: '$600'}
  ];
  firmsList: any;
  constructor(
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private loadingService: LoadingService,
    private stateService: StateService,
    private _router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private titleService: PageTitleService,
    private taxStrategyServices: ManageTaxStrategiesServices,
    private numeralService: NumeralService) {}

  ngOnInit() {
    this._init();
  }

  _init() {
    const base = this;
    this.org = Session.getOrg();
    this.user = Session.getUser().id;
    const prevState = this.stateService.getPrevState();
    if (prevState && prevState.selectedId){
      this.searchKey = prevState.selectedId;
    }
    this.getTaxStrategiesTableData(true);
    this.stateService.clearAllStates();
    this.switchBoard.onPageChangeListener.next({});
    this.switchBoard.onClickPrev.pipe(takeUntil(this.destroy$)).subscribe(title => {});
    this.switchBoard.onToggleFeedback.next({
      display: true,
      pageName: 'Manage Firms Page'
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  gotoDefaultPage() {
    const prevState = this.stateService.getPrevState();
    if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }
  }

  getTaxStrategiesTableData(refresh?) {
    this.loadingService.triggerLoadingEvent(true);
    this.titleService.setPageTitle('Firms');
    this.nextIndex = 0;
    this.hasNextSet = false;
    this.taxStrategyTableData.rows = [];
    this.resetTabData();
    this.fetchNextSet(refresh);
  }

  resetTabData() {
    this.isLoading = true;
    this.taxStrategiesSort = {};
    this.hasTaxStrategyList = false;
  }

  fetchNextSet(refresh) {
    this.loadingService.triggerLoadingEvent(true);
    this.taxStrategyServices.getTaxPlanningFirms(this.org, this.user)
      .subscribe(response => {
        this.isLoading = false;
        this.firmsList = response.firms;
        this.loadingService.triggerLoadingEvent(false);
        this.nextIndex += response.firms.length;
        this.hasNextSet = false;
        if (this.searchKey){
          this.filterData();
        } else {
          this.buildTaxStrategiesTableData(response.firms, refresh);
        }
      }, error => {
        this.handleError(error);
      });
  }

  getTabSortObject() {
    return this.taxStrategiesSort;
  }

  getSortingObject(sortObject) {
    const base = this;
    for (const key in sortObject) {
      if (sortObject.hasOwnProperty(key) && sortObject[key]) {
        return {
          sortKey: key,
          sortOrder: sortObject[key],
          fromIndex: this.nextIndex
        };
      }
    }
    return null;
  }

  buildTaxStrategiesTableData(data, refreshTable?) {
    const base = this;
    this.taxStrategyTableData.options = {
      height: '76vh',
      selectorString: 'id',
      showSearch: false,
    };
    this.taxStrategyTableData.columns = [];
    this.taxStrategyTableData.columns = this.formatColumnsForTable(this.orderArray);
    this.taxStrategyTableData.columns.push({name: 'actions', classes: 'overflow-css', title: '', type: 'html', sortable: false});
    if (refreshTable) {
      this.taxStrategyTableData.rows = [];
    } else {
      this.taxStrategyTableData.rows = this.taxStrategyTableData.rows || [];
    }
    const rows = base.taxStrategyTableData.rows || [];
    data.forEach(function(tax){
      const row = base.createTableRow(tax);
      rows.push(row);
    });
    this.taxStrategyTableData = {...this.taxStrategyTableData, rows};
    this.handleSortOrder(this.taxStrategiesSort, this.taxStrategyTableData.columns);
    if (refreshTable) {
      this.hasTaxStrategyList = false;
    }
    this.loadingService.triggerLoadingEvent(false);
    if (!(this.changeDetectorRef as ViewRef).destroyed) {
      this.changeDetectorRef.detectChanges();
    }
    if (base.taxStrategyTableData.rows.length > 0) {
      base.isLoading = false;
    } else {
      base.isLoading = false;
    }
    base.hasTaxStrategyList = true;
  }

  formatColumnsForTable(data) {
    const base = this;
    const colsArray = [];
    let orderArray;
    if (typeof data !== 'string') {
      orderArray = data;
    } else {
      orderArray = JSON.parse(data);
    }
    _.each(Object.keys(orderArray), function(key) {
      if (key === 'Name') {
        colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow', sortable: false});
      } else if (key === 'Status' || key === 'Payment Status'){
        colsArray.push({name: orderArray[key], title: key, type: 'html', classes: 'pointer zero-overflow tracker-desc', sortable: false});
      } else {
        colsArray.push({name: orderArray[key], title: key, type: 'normal', classes: 'pointer zero-overflow', sortable: false});
      }
    });
    return colsArray;
  }

  createTableRow(todo) {
    const base = this;
    const row: any = {};
    _.each(todo, function(value, key){
      if (key === 'firmName') {
        row[key] = `<a class='actionOnId' data-action='edit'>${todo[key]}</a>`;
        row.firm_name = todo[key];
      } else if (key === 'hasBillingInfo') {
        row[key] = (todo[key]) ? `<i class='icon ion-android-done' style='color: #14B5F0;left: 45px'></i>` : '';
      } else if (key === 'plan') {
        row[key] = base.numeralService.format('$0,0', todo[key]);
      }else if (key === 'promoCode') {
        row[key] = (todo[key]) ? todo[key] : '';
      }else {
        row[key] = todo[key];
      }
    });
    row.actions = `<span class='badge-tooltip'><a class='action item-download' data-action='open-firm-url'><i class='material-icons' style='color:#4554A4;font-size: 18px;position: relative;top: 2px;'>link</i><span class='bottomTooltiptext' style='font-size: 13px !important;width: 120px !important;left: 0px !important;'>Open Firm Url</span></a></span>`;

    return row;
  }

  handleSortOrder(sortOrder, columns) {
    _.each(columns, function(column){
      column.sortType = sortOrder[column.name];
    });
  }

  handleAction($event) {
    const base = this;
    const action = $event.action;
    if (action === 'delete') {
    } else if (action === 'edit') {
      this.editStrategy.id = $event.id;
      this.editStrategy.firmName = $event.firm_name;
      this.editStrategy.trialStartDate = $event.trialStartDate;
      this.editStrategy.trialEndDate = $event.trialEndDate;
      this.editStrategy.plan = $event.plan;
      this.editStrategy.status = $event.status;
      this.reveal.editFirm = true;
    } else if (action === 'open-firm-url') {
      if ($event && $event.id) {
        const firmUrl = 'https://' + $event.id + '.taxplaniq.com/wall/login';
        window.open(firmUrl);
      }
    }
  }

  handleScroll(event: NgxScrollEvent) {
    const base = this;
    if (event.isReachingBottom && !base.isLoading && !event.isWindowEvent && this.hasNextSet) {
      base.isLoading = true;
      base.fetchNextSet(false);
    }
  }

  sortByColumn(obj) {
    this.sortInTaxStrategies(obj);
  }

  sortInTaxStrategies(obj) {
    this.taxStrategiesSort[obj.column] = obj.order;
    this.resetSortOrder(this.taxStrategiesSort, this.taxStrategyTableData.columns, obj.column);
    this.nextIndex = 0;
    const sortObj = {
      sortKey: obj.column,
      sortOrder: obj.order,
      fromIndex: this.nextIndex
    };
    this.loadingService.triggerLoadingEvent(true);
    this.taxStrategyServices.getTaxPlanningFirms(this.org, this.user)
      .subscribe(response => {
        this.isLoading = false;
        this.firmsList = response.firms;
        this.taxStrategyTableData.rows = [];
        this.loadingService.triggerLoadingEvent(false);
        this.buildTaxStrategiesTableData(response.firms, true);
        this.nextIndex += response.firms.length;
        this.hasNextSet = false;
      }, error => {
        this.handleError(error);
      });
  }

  resetSortOrder(sortOrder, columns, column) {
    _.each(columns, function(col){
      if (col.name !== column) {
        sortOrder[col.name] = '';
      }
    });
  }

  addNewFirm() {
    this.stateService.addState(new State('FIRMS_LIST', this._router.url, null, null));
    this._router.navigate(['tax-planning', 'firm-creation']);
  }

  handleError(error) {
    this.loadingService.triggerLoadingEvent(false);
    error = JSON.parse(error);
    const message = error.message ? error.message : `Failed To Perform Operation`;
    try {
      this.toastService.pop(TOAST_TYPE.error, message);
    } catch (e) {
      this.toastService.pop(TOAST_TYPE.error, message);
    }
  }

  filterData(){
    if (!this.searchKey){
      this.buildTaxStrategiesTableData(this.firmsList, true);
    } else {
      const base = this;
      const list = this.firmsList || [];
      const filteredList = [];
      const columns = this.formatColumnsForTable(this.orderArray);
      _.each(list, function(strategy){
        _.each(columns, function(column){
          if(column.name != 'hasBillingInfo') {
            if (strategy[column.name] && strategy[column.name].toLowerCase().indexOf(base.searchKey.toLowerCase()) != -1){
              const tempStrategy = _.find(filteredList, {id: strategy.id});
              if (!tempStrategy){
                filteredList.push(strategy);
              }
            }
          }
        });
      });
      this.buildTaxStrategiesTableData(filteredList, true);
    }
  }

  hideReveal(key){
    this.reveal[key] = false;
  }

  updateTrialDate($event, fieldName){
    if ($event){
      this.editStrategy[fieldName] = $event;
    }
  }

  setStatus($event){
    this.editStrategy.status = $event.key;
  }

  updateFirmData() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxStrategyServices.updateTaxPlanningFirm(this.org, this.user, this.editStrategy)
      .subscribe(response => {
        this.reveal.editFirm = false;
        this.getTaxStrategiesTableData(true);
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Updated Successfully');
      }, error => {
        this.handleError(error);
      });
  }

  setPlan($event){
    this.editStrategy.plan = $event.plan;
  }

}
